import { seriesSvgBar, seriesSvgMulti } from '@d3fc/d3fc-series';
import { select, Selection, BaseType } from 'd3-selection';

import { annotationSvgGridline } from '@d3fc/d3fc-annotation';

import { PortfolioSummaryChartSeriesData } from './data/portfolioSummaryChartSeriesData';
import { SeriesPoint, Series } from 'utils/stackedSeries';
import { PortfolioSummaryChartGroupData } from './data/portfolioSummaryChartBarData';
import { BarMouseOver } from './BarMouseOver';
import { PortfolioSummarySideNetOptions } from '../store/instances/configuration/PortfolioSummaryConfigurationState';

type PortfolioSummarySeries = Series<PortfolioSummaryChartGroupData, string>[];
type PortfolioSummarySeriesPoint = SeriesPoint<PortfolioSummaryChartGroupData>;
type BarType = 'buy' | 'sell' | 'net';

/* eslint-disable @typescript-eslint/no-explicit-any */
export type PortfolioSummarySelection = Selection<
    BaseType,
    PortfolioSummarySeriesPoint,
    any,
    any
>;

export const portfolioSummaryChartSeries = (
    bandWidth: number,
    seriesData: PortfolioSummaryChartSeriesData,
    barMouseOver: BarMouseOver,
    sideNet: PortfolioSummarySideNetOptions
) => {
    const barSeries = (type: BarType) => {
        const mainValueAccessor = buildMainValueAccessor(sideNet, type);
        return seriesSvgBar()
            .orient('horizontal')
            .align(
                type === 'net' ? 'center' : type === 'sell' ? 'left' : 'right'
            )
            .bandwidth(bandWidth)
            .crossValue((d: PortfolioSummarySeriesPoint) => d.data.groupName)
            .mainValue(mainValueAccessor)
            .baseValue((d: PortfolioSummarySeriesPoint) => d[0]);
    };

    /*
     * These are the series drawn on the chart
     */
    const barDisplaySeries = (type: BarType) =>
        barSeries(type).decorate((selection: PortfolioSummarySelection) => {
            selection.enter().each((data, index, nodes) => {
                select(nodes[index])
                    .classed('portfolio-summary-bar', true)
                    .classed(type, true)
                    .classed(data.key, true);
            });
        });

    /*
     * These are the series for the legends, so we can highlight traded and remaining for a sidewith one rectangle
     */
    const barTotalSeriesForLegends = (type: BarType) =>
        barSeries(type).decorate((selection: PortfolioSummarySelection) => {
            selection.enter().each((_data, index, nodes) => {
                const barSelection = select(nodes[index])
                    .classed('portfolio-summary-bar-section', true)
                    .classed(type, true);

                // Listen to mouse events for the legends...
                barSelection
                    .on('mouseenter', (event, d) => {
                        const dataItem = d as PortfolioSummarySeriesPoint;
                        barSelection.classed('selected', true);

                        barMouseOver.publish({
                            bar: type,
                            key: dataItem.key,
                            groupName: dataItem.data.groupName,
                            y: event.offsetY,
                        });
                    })
                    .on('mouseleave', () => {
                        barSelection.classed('selected', false);
                        barMouseOver.publish({});
                    });
            });
        });

    function buildSeries(type: BarType, isTotal = false) {
        const key = (type +
            (isTotal ? 'Total' : '')) as keyof PortfolioSummaryChartSeriesData;

        const s = isTotal
            ? barTotalSeriesForLegends(type)
            : barDisplaySeries(type);
        return seriesSvgMulti()
            .mapping(
                (data: PortfolioSummaryChartSeriesData, index: number) =>
                    (data[key] as PortfolioSummarySeries)[index]
            )
            .series((seriesData[key] as PortfolioSummarySeries).map(() => s));
    }

    const buySeries = buildSeries('buy');
    const buyTotalSeries = buildSeries('buy', true);
    const sellSeries = buildSeries('sell');
    const sellTotalSeries = buildSeries('sell', true);
    const netSeries = buildSeries('net');
    const netTotalSeries = buildSeries('net', true);

    return seriesSvgMulti().series([
        annotationSvgGridline().yTicks(),
        buySeries,
        sellSeries,
        netSeries,
        buyTotalSeries,
        sellTotalSeries,
        netTotalSeries,
    ]);
};

function buildMainValueAccessor(
    sideNet: PortfolioSummarySideNetOptions,
    bar: BarType
) {
    // Set all values of hidden bars to zero to avoid mouseOver wars...
    let displayBar = true;
    if (sideNet === 'side' && bar === 'net') {
        displayBar = false;
    }
    if (sideNet === 'net' && bar !== 'net') {
        displayBar = false;
    }
    return (d: PortfolioSummarySeriesPoint) => (displayBar ? d[1] : 0);
}
